import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../api/remembrApi';
import { logger, perf } from '../../utils/logger';
import type { RootState } from '../../store/store';
import type {
  UploadedFile,
  InventoryItem,
  FileWithInventoryResponse,
  InventoryItemResponse,
  UpdateInventoryItemRequest,
  EnhancedInventoryItem,
  EnhancedInventoryRequest,
  CustodyFlipResponse,
} from '../../api/types';
import {
  handoffItem,
  recallItem,
  returnItem,
  acknowledgeRecall,
} from '../inventory/custodySlice';

/**
 * FE-255 PR2 — optimistic-mutation bookkeeping for custody flips.
 *
 * Keyed by the thunk's auto-generated `action.meta.requestId`, so two
 * mutations on different items in flight at the same time roll back
 * independently. Cleared on .fulfilled and .rejected.
 *
 * We snapshot ALL custody-related fields we mutate so .rejected can
 * restore byte-for-byte (avoid the "I rolled back partial state and
 * broke an invariant" class of bug). For recall, only
 * `has_active_recall` is touched today, but we still capture the
 * other fields cheaply so we don't have to grow this type when the
 * BE eventually flips custody on recall fulfillment.
 */
type BrowseCustodyInflight = {
  itemId: number;
  prev: {
    current_custodian_user_id: number | null | undefined;
    custody_since: string | null | undefined;
    custody_due_date: string | null | undefined;
    has_active_recall: boolean | undefined;
  };
};

/**
 * FE-255 PR4 — optimistic-mutation bookkeeping for item edits
 * (`updateInventoryItem`).
 *
 * Same shape as BrowseCustodyInflight, but stores the whole prev row
 * for each array we patched. Snapshotting the full row (instead of
 * just the keys present in `updateData`) keeps the rollback path
 * dumb: if .rejected fires, replace the row byte-for-byte. We can't
 * partially undo a partial patch without juggling sentinel values
 * for "was undefined" vs "was missing".
 *
 * Keyed by the thunk's auto-generated `action.meta.requestId` so
 * two edits on different items in flight at the same time roll back
 * independently. Cleared on .fulfilled and .rejected.
 */
type BrowseItemEditInflight = {
  itemId: number;
  /** Snapshot of the row in `inventoryItemsEnhanced[]` (the canonical
   *  enhanced array). null = row not found at .pending time (we still
   *  record the slot so .rejected is a no-op rather than crashing). */
  prevEnhanced: InventoryItemResponse | null;
  /** Snapshot of the row in legacy `inventoryItems[]` (kept in sync
   *  for screens still on the older shape). null = not in legacy array. */
  prevLegacy: InventoryItem | null;
};

/**
 * Merge a partial patch into a row, skipping `undefined` values so
 * we don't accidentally CLEAR a field that wasn't part of the patch.
 *
 * `{...row, ...patch}` would turn a field into `undefined` if the
 * patch object happens to carry the key with an undefined value
 * (which Object.assign / spread both do). That's a real footgun
 * with `Partial<>` payloads built from optional form fields. This
 * helper preserves the existing value when the patch key is
 * undefined; pass `null` explicitly when you do want to clear.
 */
function applyPartialPatch<T extends object>(row: T, patch: Partial<T>): T {
  const out: any = { ...row };
  for (const k in patch) {
    const v = (patch as any)[k];
    if (v !== undefined) out[k] = v;
  }
  return out;
}
/**
 * Filter options used by BrowseScreen + browseSlice. Previously lived
 * alongside the legacy FilterBar component; inlined here when the
 * component was retired in the Fidelity Purple cleanup pass.
 */
export interface FilterOptions {
  search: string;
  category?: string;
  is_verified?: boolean;
  min_value?: number;
  max_value?: number;
  sort_by?: 'date' | 'value' | 'name' | 'confidence';
  sort_order?: 'asc' | 'desc';
  // Status filters
  sharing_status?: 'owned' | 'borrowed' | 'lent' | 'public';
  is_public?: boolean;
  is_shareable?: boolean;
  /** FE-251 — items scope (4-pill UI). Four mutually-disjoint
   *  quadrants of the (owner, custodian) lattice. Together they
   *  reconstitute "all items":
   *
   *   - 'all'      : everything — owned ∪ held. Default.
   *   - 'mine'     : owned AND currently in my possession (NOT lent out).
   *   - 'lent'     : owned AND currently held by someone else.
   *   - 'borrowed' : NOT owned AND currently held by me (in-crew handoffs).
   *
   *  Backward-compatible aliases (older saved state may still hold these):
   *   - 'owned' → coerced to 'all' on first read (legacy "Just owned" tab).
   *   - 'held'  → coerced to 'borrowed' (legacy "Just held" tab).
   *   - 'both'  → coerced to 'all' (legacy widest set).
   *
   *  BE request mapping:
   *   - `held_by_me=true` → 'all', 'borrowed' (BE returns the union)
   *   - no `held_by_me`    → 'mine', 'lent' (BE returns owned-only)
   *
   *  Client-side filter applied AFTER fetch:
   *   - 'all'      : passthrough
   *   - 'mine'     : keep if custodian_id == me OR custodian_id == null
   *   - 'lent'     : keep if custodian_id != me AND custodian_id != null
   *   - 'borrowed' : keep if held-by-me-but-not-owned (is_borrowed OR
   *                  (custodian==me AND owner!=me))
   */
  scope?: BrowseScope;
  /** FE #257 — Tag-typeahead multi-select.  Empty array (default) =
   *  no tag filter.  Multiple values are AND-intersected — Linear's
   *  saved-view filter formula semantics.  Used by the Tag layout's
   *  TagTypeahead component to scope the visible items to those
   *  whose `tags` field includes EVERY string in this array. */
  tags?: string[];
  /** FE #256 — Location-typeahead single-select.  When set, the
   *  Recent layout filters items to those whose `location` matches
   *  case-insensitively.  Driven by `LocationTypeahead` on the
   *  Location layout — picking a suggestion sets this AND switches
   *  the parent's layout pill back to Recent so the user lands on
   *  the filtered grid (Linear's "view-scoped filter with expand-
   *  to-global escape hatch" pattern). */
  location?: string;
}

export type BrowseScope = 'all' | 'mine' | 'lent' | 'borrowed';

/** FE-251 — normalize any legacy/unknown scope value to one of the
 *  four canonical buckets. Safe to call on every read so cached state
 *  from a prior FE version doesn't break the new selector. */
export function normalizeScope(raw: unknown): BrowseScope {
  switch (raw) {
    case 'all':
    case 'mine':
    case 'lent':
    case 'borrowed':
      return raw;
    // Legacy aliases.
    case 'owned':
      // Old "Just owned" tab = everything you own. Closest mapping
      // is the widest set ('all'); user can drill down with 'mine'
      // or 'lent' once they see the new pills.
      return 'all';
    case 'held':
      return 'borrowed';
    case 'both':
      return 'all';
    default:
      return 'all';
  }
}

/** Sprint 2.5 (FE-9.4.1) — dedicated cache for the Scenes layout.
 *  Decoupled from `currentPage`/`hasMore`/`isLoading` because Recent
 *  + Scenes layouts both paginate independently and would otherwise
 *  fight over the shared fields when the user toggles between them. */
export interface BrowseScenesCache {
  /** Server-side scene-grouped data — each scene is a FileRecord
   *  with its nested inventory_items[] (no client-side groupBy). */
  scenes: FileWithInventoryResponse[];
  /** Pagination cursors. nextOffset is what we send on the next
   *  Load-more click. */
  nextOffset: number;
  total: number;
  hasMore: boolean;
  loading: boolean;
  /** True after the first response (success or fail) — drives the
   *  empty-state vs initial-spinner branch. */
  hasLoaded: boolean;
  error: string | null;
}

interface BrowseState {
  // Legacy data (for backward compatibility)
  files: UploadedFile[];
  inventoryItems: InventoryItem[];
  selectedFile: UploadedFile | null;

  // Enhanced data structures
  filesWithInventory: FileWithInventoryResponse[];
  inventoryItemsEnhanced: InventoryItemResponse[];
  enhancedInventoryItems: EnhancedInventoryItem[];

  /** FE-255 PR2 — see BrowseCustodyInflight comment at top of file. */
  custodyInflight: Record<string, BrowseCustodyInflight>;
  /** FE-255 PR4 — see BrowseItemEditInflight comment at top of file. */
  itemEditInflight: Record<string, BrowseItemEditInflight>;

  /** Sprint 2.5 (FE-9.4.1) — Scenes layout cache. */
  scenesCache: BrowseScenesCache;

  // UI state
  viewMode: 'files' | 'inventory' | 'relationship';
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  error: string | null;
  
  // Delete operation states
  deletingFileIds: string[];
  deletingItemIds: string[];
  
  // Filtering and search
  filters: FilterOptions;
  categories: string[];
  isFilterExpanded: boolean;
  
  // Metadata
  totalCount: number;
  lastUpdated: string | null;
}

const initialState: BrowseState = {
  // Legacy data (for backward compatibility)
  files: [],
  inventoryItems: [],
  selectedFile: null,
  
  // Enhanced data structures
  filesWithInventory: [],
  inventoryItemsEnhanced: [],
  enhancedInventoryItems: [],

  // FE-255 PR2 — optimistic-custody bookkeeping (empty until a
  // handoff / return / recall / acknowledgeRecall fires).
  custodyInflight: {},
  itemEditInflight: {},

  // Sprint 2.5 (FE-9.4.1) — Scenes layout cache
  scenesCache: {
    scenes: [],
    nextOffset: 0,
    total: 0,
    hasMore: true,
    loading: false,
    hasLoaded: false,
    error: null,
  },

  // UI state
  viewMode: 'inventory',
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  currentPage: 0,
  error: null,
  
  // Delete operation states
  deletingFileIds: [],
  deletingItemIds: [],
  
  // Filtering and search
  filters: {
    search: '',
    sort_by: 'date',
    sort_order: 'desc',
    // FE-251 — 4-pill scope. Default is 'all' (the widest set) so a
    // fresh user sees everything they own + hold without having to
    // click a pill. BE-210 has been stable for months; held_by_me=true
    // returns the union cleanly.
    scope: 'all',
  },
  categories: [],
  isFilterExpanded: false,
  
  // Metadata
  totalCount: 0,
  lastUpdated: null,
};

/**
 * Sprint 2.5 (FE-9.4.2) — bumped from 20 → 100.
 *
 * Backend `/users/inventory` `limit` cap is 200 (verified via OpenAPI).
 * 100 is comfortably under that and gives the client-side groupings
 * (Tag / Location) enough items to render meaningfully on a 2,760-item
 * account without forcing the user to scroll Recent first.
 *
 * Recent layout still paginates normally (Load more on scroll), so
 * users with > 100 items aren't stuck.
 *
 * NOTE: this constant ALSO drives the legacy `loadFiles` /
 * `loadInventory` / `loadFilesWithInventory` thunks below. Reducing
 * it here would affect those paths too. For the enhanced inventory
 * loader specifically — which dominates first-paint time on Items
 * tab — use `INVENTORY_PAGE_SIZE` instead (see below).
 */
const ITEMS_PER_PAGE = 100;

/**
 * Perf hotfix — first-paint optimization for `loadInventoryItemsEnhanced`.
 *
 * The enhanced inventory endpoint returns per-row signed URLs,
 * mask URLs, photo summaries, and custody metadata, so the BE has
 * to assemble a heavy payload per item. With `limit=100` on a 2,968-
 * item account, that single request took ~19s end-to-end (measured
 * via DevTools Network tab on dev, 2026-05-26 — see PR description).
 *
 * 10 rows gives the user a partial-screen-worth of cards instantly
 * and lets `loadInventoryItemsEnhanced` paginate continuously as the
 * user scrolls (FlatList's onEndReached fires when 2-3 cards from
 * the bottom, so the next batch is fetched before the user reaches
 * the spinner). On a 2-column phone grid this is 5 visible rows on
 * first paint, which is enough to anchor the layout but small
 * enough that ~3-4× faster than 20 on the same dev BE.
 *
 *   - 100 (was): ~19s first paint, blocks completely
 *   - 20  (tried): ~4s, still a perceptible wait
 *   - 10  (now): ~2s, feels like infinite scroll
 *
 * We keep `ITEMS_PER_PAGE = 100` for the legacy thunks because their
 * UIs assume coarser pagination and changing them is out of scope
 * for this perf pass.
 */
const INVENTORY_PAGE_SIZE = 10;

// Async thunks
export const loadFiles = createAsyncThunk(
  'browse/loadFiles',
  async (refresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      // For refresh, start from page 0. For load more, use current file count to calculate skip
      const skip = refresh ? 0 : state.browse.files.length;
      const page = Math.floor(skip / ITEMS_PER_PAGE);
      
      if (__DEV__) {
        console.log('🔍 loadFiles request:', {
          refresh,
          currentPageInState: state.browse.currentPage,
          calculatedPage: page,
          skip,
          currentFilesCount: state.browse.files.length
        });
      }
      
      const response = await api.getFiles(skip, ITEMS_PER_PAGE);
      
      if (__DEV__) {
        console.log('📡 loadFiles API response:', {
          itemsReceived: response.items.length,
          totalInAPI: response.total,
          currentSkip: response.skip,
          limit: response.limit,
          hasNext: response.has_next
        });
      }
      
      return {
        files: response.items,
        hasMore: response.has_next ?? (response.items.length === ITEMS_PER_PAGE), // Use has_next if available
        page: Math.floor((skip + response.items.length) / ITEMS_PER_PAGE),
        refresh,
      };
    } catch (error: any) {
      logger.error('Failed to load files', error);
      
      let errorMessage = 'Failed to load files';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || 'Unknown error');
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const loadInventoryItems = createAsyncThunk(
  'browse/loadInventoryItems',
  async (refresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const page = refresh ? 0 : state.browse.currentPage;
      const skip = page * ITEMS_PER_PAGE;
      
      const response = await api.getInventory(skip, ITEMS_PER_PAGE);
      
      return {
        items: response.items,
        hasMore: response.items.length === ITEMS_PER_PAGE,
        page: page + 1,
        refresh,
      };
    } catch (error: any) {
      logger.error('Failed to load inventory items', error);
      
      let errorMessage = 'Failed to load inventory items';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || 'Unknown error');
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteSelectedFile = createAsyncThunk(
  'browse/deleteFile',
  async (fileId: string, { rejectWithValue }) => {
    console.log('🗑️ THUNK: deleteSelectedFile called with fileId:', fileId);
    try {
      console.log('🗑️ THUNK: Calling api.deleteFile...');
      const response = await api.deleteFile(fileId);
      console.log('🗑️ THUNK: API response received:', response);
      
      logger.info('File deleted successfully', { 
        fileId, 
        deletedItemsCount: response.deleted_items_count,
        vectorResults: response.vector_deletion_results
      });
      return response;
    } catch (error: any) {
      console.log('🗑️ THUNK: API call failed:', error);
      logger.error('Failed to delete file', error);
      
      let errorMessage = 'Failed to delete file';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || 'Unknown error');
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log('🗑️ THUNK: Returning error:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteInventoryItem = createAsyncThunk(
  'browse/deleteInventoryItem',
  async (itemId: string, { rejectWithValue }) => {
    console.log('🗑️ THUNK: deleteInventoryItem called with itemId:', itemId);
    try {
      console.log('🗑️ THUNK: Calling api.deleteInventoryItem...');
      const response = await api.deleteInventoryItem(itemId);
      console.log('🗑️ THUNK: API response received:', response);
      
      logger.info('Inventory item deleted successfully', { 
        itemId, 
        vectorResults: response.vector_deletion_results
      });
      return response;
    } catch (error: any) {
      console.log('🗑️ THUNK: API call failed:', error);
      logger.error('Failed to delete inventory item', error);
      
      let errorMessage = 'Failed to delete item';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || 'Unknown error');
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log('🗑️ THUNK: Returning error:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateInventoryItem = createAsyncThunk(
  'browse/updateInventoryItem',
  async (
    { itemId, updateData }: { itemId: number; updateData: UpdateInventoryItemRequest },
    { rejectWithValue }
  ) => {
    console.log('📝 THUNK: updateInventoryItem called with itemId:', itemId, 'updateData:', updateData);
    try {
      console.log('📝 THUNK: Calling api.updateInventoryItem...');
      const response = await api.updateInventoryItem(itemId, updateData);
      console.log('📝 THUNK: API response received:', response);
      
      logger.info('Inventory item updated successfully', { 
        itemId, 
        updateData,
        updatedItem: response
      });
      return response;
    } catch (error: any) {
      console.log('📝 THUNK: API call failed:', error);
      logger.error('Failed to update inventory item', error);
      
      let errorMessage = 'Failed to update item';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || 'Unknown error');
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log('📝 THUNK: Returning error:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Enhanced API thunks for new functionality
export const loadFilesWithInventory = createAsyncThunk(
  'browse/loadFilesWithInventory',
  async (
    { refresh = false, contentType }: { refresh?: boolean; contentType?: 'image' | 'text' } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const page = refresh ? 1 : state.browse.currentPage + 1;

      const response = await api.getFilesWithInventory(page, ITEMS_PER_PAGE);

      return {
        files: response.files,
        pagination: response.pagination,
        refresh,
      };
    } catch (error: any) {
      logger.error('Failed to load files with inventory', error);
      return rejectWithValue('Failed to load files with inventory');
    }
  }
);

/**
 * Sprint 2.5 (FE-9.4.1) — `loadScenesPage`
 *
 * Dedicated thunk for the Browse Scenes layout. Pages over the
 * server-side scene-grouped data (`/users/files-with-inventory`)
 * with `limit` + `offset` cursors. Decoupled from `currentPage`
 * (Recent layout) so the two pillars don't fight for state.
 *
 * Two modes:
 *   - `'replace'` (default for offset=0) wipes the cache + loads a
 *     fresh first page. Used for cold-load + pull-to-refresh.
 *   - `'append'` adds the new page after the existing scenes.
 *     Used for "Load more scenes" button.
 *
 * Defensive dedupe (backend#37):
 * Until the backend's duplicate-FileRecord bug is fixed, we may
 * receive the same file twice (created milliseconds apart). The
 * reducer dedupes by id, then by `(filename, created_at within 5s)`
 * — keeps the row with more `inventory_items`, drops the rest.
 * Once #37 lands, this becomes a no-op safety net.
 */
const SCENES_PAGE_SIZE = 20;

export const loadScenesPage = createAsyncThunk<
  {
    scenes: FileWithInventoryResponse[];
    total: number;
    nextOffset: number;
    hasMore: boolean;
    mode: 'replace' | 'append';
  },
  { mode?: 'replace' | 'append'; pageSize?: number } | undefined
>('browse/loadScenesPage', async (args, { getState, rejectWithValue }) => {
  const mode = args?.mode ?? 'replace';
  const pageSize = args?.pageSize ?? SCENES_PAGE_SIZE;

  try {
    const state = getState() as RootState;
    const offset =
      mode === 'replace' ? 0 : state.browse.scenesCache.nextOffset;
    // Convert offset back to a page number for the existing API
    // method (which translates page → offset internally). Keeps
    // the wire call consistent with the legacy
    // loadFilesWithInventory path.
    const page = Math.floor(offset / pageSize) + 1;
    const response = await api.getFilesWithInventory(page, pageSize);

    return {
      scenes: response.files,
      total: response.pagination?.total ?? response.files.length,
      nextOffset: offset + pageSize,
      hasMore: response.pagination?.has_next ?? false,
      mode,
    };
  } catch (error: any) {
    logger.error('Failed to load scenes page', error);
    return rejectWithValue(
      error?.response?.data?.detail ||
        error?.message ||
        'Failed to load snapshots',
    );
  }
});

/**
 * Hotfix — single-scene fetcher for SceneViewerScreen.
 *
 * Before this, SceneViewerScreen relied entirely on `loadScenesPage`
 * (page 1, 20 scenes) to populate the cache. Deep-linking to any
 * scene whose row isn't in page 1 (notifications, ItemDetail
 * "scanned from this scene" jump, refresh on /scenes/{id}, etc.)
 * left the user stuck on a perpetual "Loading scene…" spinner
 * because `selectScenes.find(s => s.id === fileId)` returned
 * undefined and the load effect's guard (`scenes.length === 0`)
 * was already false after page 1 returned.
 *
 * This thunk hits `/users/files/{fileId}` directly via the existing
 * `getFileById` API method (response shape matches the page payload's
 * elements). On fulfilled, the reducer upserts the row into
 * `scenesCache.scenes` — keyed by id so re-fetches don't duplicate.
 */
export const loadSceneById = createAsyncThunk<
  FileWithInventoryResponse,
  { fileId: number }
>('browse/loadSceneById', async ({ fileId }, { rejectWithValue }) => {
  try {
    const response = await api.getFileById(fileId);
    return response;
  } catch (error: any) {
    logger.error('Failed to load scene by id', error);
    return rejectWithValue(
      error?.response?.data?.detail ||
        error?.message ||
        'Failed to load snapshot',
    );
  }
});

/**
 * FE-210 — Delete a scene (FileRecord) from Browse > Scenes.
 *
 * BE-205 contract:
 *   DELETE /users/files/{file_id}?cascade=false (default)
 *     → 204 No Content
 *     → FileRecord deleted, linked inventory rows orphan
 *       (source_file_id := NULL) but stay in inventory.
 *   DELETE /users/files/{file_id}?cascade=true
 *     → 204 No Content
 *     → FileRecord + every linked inventory row deleted.
 *
 * On fulfilled, the reducer:
 *   - Drops the file from `scenesCache.scenes` (optimistic-ish —
 *     we only do it after the server confirms because cascade
 *     destroys data).
 *   - When `cascade=true`, also drops every loaded inventory row
 *     whose `source_file_id === fileId` from `inventoryItemsEnhanced`.
 *   - When `cascade=false`, sets `source_file_id := null` on those
 *     rows so they no longer attribute to the dead scene.
 *   - Decrements `scenesCache.total` by 1.
 */
export const deleteScene = createAsyncThunk<
  { fileId: number; cascade: boolean; items_affected: number },
  { fileId: number; cascade: boolean }
>('browse/deleteScene', async ({ fileId, cascade }, { rejectWithValue }) => {
  try {
    const result = await api.deleteScene(fileId, { cascade });
    return { fileId, cascade, items_affected: result.items_affected };
  } catch (error: any) {
    logger.error('Failed to delete scene', error);
    return rejectWithValue(
      error?.response?.data?.detail ||
        error?.message ||
        'Failed to delete snapshot',
    );
  }
});

/**
 * Internal — dedupes a list of FileRecord rows for backend#37
 * defense. Groups by `filename` and timestamps within 5s; keeps
 * the row with the most `inventory_items` (the "real" save).
 *
 * Pure function, exported for testability.
 */
export function dedupeScenes(
  rows: FileWithInventoryResponse[],
): FileWithInventoryResponse[] {
  // Bucket by filename
  const buckets = new Map<string, FileWithInventoryResponse[]>();
  const noFilename: FileWithInventoryResponse[] = [];
  for (const row of rows) {
    if (!row.filename) {
      noFilename.push(row);
      continue;
    }
    const bucket = buckets.get(row.filename) ?? [];
    bucket.push(row);
    buckets.set(row.filename, bucket);
  }

  const out: FileWithInventoryResponse[] = [...noFilename];
  for (const bucket of buckets.values()) {
    if (bucket.length === 1) {
      out.push(bucket[0]);
      continue;
    }
    // Multiple rows for the same filename. Group by created_at
    // within 5 seconds; keep the one with the most items in each
    // tight cluster.
    bucket.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
    const clusters: FileWithInventoryResponse[][] = [];
    for (const row of bucket) {
      const last = clusters[clusters.length - 1];
      if (
        !last ||
        Math.abs(
          new Date(row.created_at).getTime() -
            new Date(last[last.length - 1].created_at).getTime(),
        ) > 5_000
      ) {
        clusters.push([row]);
      } else {
        last.push(row);
      }
    }
    for (const cluster of clusters) {
      cluster.sort(
        (a, b) => (b.inventory_items?.length ?? 0) - (a.inventory_items?.length ?? 0),
      );
      out.push(cluster[0]);
      if (cluster.length > 1) {
        // eslint-disable-next-line no-console
        console.warn(
          `[scenes] dedupe dropped ${cluster.length - 1} duplicate FileRecord(s) for ${cluster[0].filename} — backend#37`,
        );
      }
    }
  }

  // Stable sort by created_at desc so most-recent scenes come first
  out.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  return out;
}

export const loadInventoryItemsEnhanced = createAsyncThunk(
  'browse/loadInventoryItemsEnhanced',
  async (
    { 
      refresh = false, 
      filters = {} 
    }: {
      refresh?: boolean;
      filters?: Partial<FilterOptions>
    } = {},
    { getState, rejectWithValue, signal }
  ) => {
    try {
      const state = getState() as RootState;
      // Use inventory items count for pagination, similar to files
      const currentItemsCount = state.browse.inventoryItemsEnhanced.length;
      // Perf hotfix — page-number derivation must match the new
      // INVENTORY_PAGE_SIZE so pagination stays continuous (offset
      // still uses currentItemsCount.length below, but `page` is
      // logged + forwarded for telemetry).
      const page = refresh ? 1 : Math.floor(currentItemsCount / INVENTORY_PAGE_SIZE) + 1;
      const currentFilters = { ...state.browse.filters, ...filters };
      
      // Only log in development and for debugging
      if (__DEV__) {
        console.log('🔍 loadInventoryItemsEnhanced - Request:', {
          refresh,
          page,
          hasMore: state.browse.hasMore,
          currentItems: state.browse.inventoryItemsEnhanced.length
        });
      }
      
      const apiFilters: any = {};
      
      // Map FilterOptions to API filters
      if (currentFilters.category) {
        apiFilters.category = currentFilters.category;
      }
      if (currentFilters.is_verified !== undefined) {
        apiFilters.verified_only = currentFilters.is_verified;
      }
      if (currentFilters.min_value !== undefined) {
        apiFilters.min_value = currentFilters.min_value;
      }
      if (currentFilters.max_value !== undefined) {
        apiFilters.max_value = currentFilters.max_value;
      }
      if (currentFilters.search) {
        apiFilters.search = currentFilters.search;
      }
      
      // Map status filters to API parameters
      if (currentFilters.sharing_status) {
        apiFilters.sharing_status = currentFilters.sharing_status;
      }
      if (currentFilters.is_public !== undefined) {
        apiFilters.is_public = currentFilters.is_public;
      }
      if (currentFilters.is_shareable !== undefined) {
        apiFilters.is_shareable = currentFilters.is_shareable;
      }

      // issue #324 / #167 — server-side scope partition. Map each of the
      // 4 pills to the BE `scope` param so a single paginated request
      // returns EXACTLY that pill's rows. Replaces the old hybrid
      // (held_by_me for 2 pills + a client-side `.filter()` for the other
      // 2), which produced empty pages / wrong counts on large accounts
      // because matching rows lived on later pages (Lent out = 1 of 954).
      const scope = normalizeScope(currentFilters.scope);
      const SCOPE_TO_API: Record<
        BrowseScope,
        NonNullable<EnhancedInventoryRequest['scope']>
      > = {
        all: 'all',
        mine: 'in_my_possession',
        lent: 'lent_out',
        borrowed: 'borrowed',
      };
      apiFilters.scope = SCOPE_TO_API[scope];

      // FE-251 — sort_by=updated_at applied ACROSS ALL SCOPES per the
      // user's "latest activity first" requirement. Previously this
      // only applied to held/both because the BE recommendation in
      // backend#247 was specifically for the custody-flip surfacing.
      // The same property — most-recent activity at the top — is
      // exactly what we want everywhere: a new scan, a borrow
      // approve, a hand back, a photo upload, any PATCH bumps
      // updated_at, so the most recently-touched item leads.
      const enhancedFilters: EnhancedInventoryRequest = {
        // Perf hotfix — was ITEMS_PER_PAGE (100). Switched to
        // INVENTORY_PAGE_SIZE (20) because the enhanced endpoint is
        // ~5x slower per-row than the basic list (signed URLs +
        // mask URLs + photos[] all served per item). Smaller first
        // page = faster first paint; user pagination on scroll
        // already handles subsequent batches.
        limit: INVENTORY_PAGE_SIZE,
        offset: refresh ? 0 : currentItemsCount,
        sort_order: 'desc',
        sort_by: 'updated_at',
        ...apiFilters,
      };

      // FE-251 — bracket the inventory load so we can see end-to-end
      // duration per scope change. Logs:
      //   [PERF] start thunk:loadInventory(scope=owned,refresh=true)
      //   [PERF] end   thunk:loadInventory(...) (430ms)  status: 200, count: 20
      const response = await perf.span(
        `thunk:loadInventory(scope=${scope},refresh=${refresh})`,
        () => api.getEnhancedInventory(enhancedFilters, { signal }),
        { offset: enhancedFilters.offset, limit: enhancedFilters.limit, page },
      );

      perf.mark('loadInventory:resolved', {
        scope,
        refresh,
        returned: response.inventory?.length ?? 0,
        total: response.pagination?.total ?? null,
        hasNext: response.pagination?.has_next ?? null,
      });

      return {
        items: response.inventory,
        pagination: response.pagination,
        filters: currentFilters,
        refresh,
      };
    } catch (error: any) {
      // Aborted requests reach this catch (axios throws CanceledError when
      // the AbortSignal fires). Don't log/report — the rejected reducer
      // checks action.meta.aborted and skips state updates so the newer
      // in-flight request wins cleanly.
      if (signal.aborted) {
        return rejectWithValue('aborted');
      }
      console.error('❌ loadInventoryItemsEnhanced failed:', error);
      logger.error('Failed to load enhanced inventory items', error);
      return rejectWithValue('Failed to load inventory items');
    }
  }
);

export const loadCategories = createAsyncThunk(
  'browse/loadCategories',
  async (_, { rejectWithValue }) => {
    try {
      // Extract categories from existing inventory items
      const response = await api.getUserInventory(1, 200); // Load maximum allowed batch to get categories
      
      // Handle different response structures
      let allItems: InventoryItemResponse[] = [];
      if ('inventory' in response && Array.isArray(response.inventory)) {
        allItems = response.inventory as InventoryItemResponse[];
      } else if ('files' in response && Array.isArray(response.files)) {
        allItems = response.files.flatMap(file => file.inventory_items);
      }
      const categories = Array.from(
        new Set(
          allItems
            .map((item: InventoryItemResponse) => item.item_category)
            .filter((cat): cat is string => Boolean(cat))
        )
      ).sort();
      
      return categories;
    } catch (error: any) {
      logger.error('Failed to load categories', error);
      return rejectWithValue('Failed to load categories');
    }
  }
);

// Slice
const browseSlice = createSlice({
  name: 'browse',
  initialState,
  reducers: {
    setViewMode: (state, action: PayloadAction<'files' | 'inventory' | 'relationship'>) => {
      // Only clear data if view mode is actually changing
      if (state.viewMode !== action.payload) {
        state.viewMode = action.payload;
        state.currentPage = 0;
        state.hasMore = true;
        // Clear existing data to prevent pagination confusion
        state.files = [];
        state.inventoryItems = [];
        state.filesWithInventory = [];
        state.inventoryItemsEnhanced = [];
        state.totalCount = 0;
        state.error = null;
        state.isLoading = false;
        state.isLoadingMore = false;
      }
    },
    setSelectedFile: (state, action: PayloadAction<UploadedFile | null>) => {
      state.selectedFile = action.payload;
    },
    setFilters: (state, action: PayloadAction<FilterOptions>) => {
      state.filters = action.payload;
      state.currentPage = 0;
      state.hasMore = true;
    },
    toggleFilterExpanded: (state) => {
      state.isFilterExpanded = !state.isFilterExpanded;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetBrowseState: (state) => {
      state.files = [];
      state.inventoryItems = [];
      state.filesWithInventory = [];
      state.inventoryItemsEnhanced = [];
      state.enhancedInventoryItems = [];
      state.currentPage = 0;
      state.hasMore = true;
      state.error = null;
      state.totalCount = 0;
      state.lastUpdated = null;
    },
    /**
     * Synchronous in-place merge for a single item. Used by ItemDetailScreen
     * after an EditItemSheet save so the Browse list reflects the edit
     * without a full refetch. Payload shape matches the API response from
     * updateInventoryItem (i.e. InventoryItemResponse).
     */
    mergeInventoryItem: (state, action: PayloadAction<any>) => {
      const updated = action.payload;
      if (!updated?.id) return;
      const i = state.inventoryItemsEnhanced.findIndex(
        (it: any) => it.id === updated.id,
      );
      if (i !== -1) {
        state.inventoryItemsEnhanced[i] = {
          ...state.inventoryItemsEnhanced[i],
          ...updated,
        } as any;
      }
      const j = state.enhancedInventoryItems.findIndex(
        (it: any) => it.id === updated.id,
      );
      if (j !== -1) {
        state.enhancedInventoryItems[j] = {
          ...state.enhancedInventoryItems[j],
          ...updated,
        } as any;
      }
      state.lastUpdated = new Date().toISOString();
    },
    /**
     * Upsert an item by id with prepend semantics. Used by the
     * streaming scan flow (streamScan → UploadScreen): each SSE
     * `item` event is already persisted server-side, and the user
     * should see it at the TOP of the Items grid as soon as it
     * arrives — no refetch needed.
     *
     * If the id already exists (re-scan, edit round-trip, whatever)
     * we replace in place; otherwise we unshift. totalCount advances
     * monotonically so the Dashboard subtitle bumps in real time.
     */
    upsertInventoryItem: (state, action: PayloadAction<any>) => {
      const it = action.payload;
      if (!it?.id) return;
      const i = state.inventoryItemsEnhanced.findIndex(
        (x: any) => x.id === it.id,
      );
      if (i !== -1) {
        state.inventoryItemsEnhanced[i] = it as any;
      } else {
        state.inventoryItemsEnhanced.unshift(it as any);
      }
      const j = state.enhancedInventoryItems.findIndex(
        (x: any) => x.id === it.id,
      );
      if (j !== -1) {
        state.enhancedInventoryItems[j] = it as any;
      } else {
        state.enhancedInventoryItems.unshift(it as any);
      }
      state.totalCount = Math.max(
        state.totalCount,
        state.inventoryItemsEnhanced.length,
      );
      state.lastUpdated = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    // Load files
    builder
      .addCase(loadFiles.pending, (state, action) => {
        if (action.meta.arg) { // refresh
          state.isLoading = true;
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(loadFiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        
        if (action.payload.refresh) {
          state.files = action.payload.files;
          if (__DEV__) {
            console.log('🔄 loadFiles refresh: Replaced all files, new count:', state.files.length);
          }
        } else {
          // Filter out duplicates before adding new files
          const existingIds = state.files.map(file => file.id);
          const newFiles = action.payload.files.filter(file => !existingIds.includes(file.id));
          
          if (__DEV__) {
            console.log('📥 loadFiles pagination:', {
              previousCount: state.files.length,
              newFilesReceived: action.payload.files.length,
              duplicatesFiltered: action.payload.files.length - newFiles.length,
              newUniqueFiles: newFiles.length,
              sampleExistingIds: existingIds.slice(0, 3),
              sampleNewFileIds: action.payload.files.slice(0, 3).map(f => f.id)
            });
            
            // Detect backend issue: if we get no new files multiple times, stop pagination
            if (newFiles.length === 0 && action.payload.files.length > 0) {
              console.warn('🚨 API BACKEND ISSUE: Received duplicate files. Backend may be ignoring page parameter.');
              console.warn('🛑 Stopping pagination to prevent endless loop.');
              state.hasMore = false;
              state.error = 'API pagination issue: receiving duplicate data';
              return;
            }
          }
          
          state.files.push(...newFiles);
          
          if (__DEV__) {
            console.log('✅ loadFiles final count:', state.files.length);
          }
        }
        
        state.hasMore = action.payload.hasMore;
        state.currentPage = action.payload.page;
        state.error = null;
      })
      .addCase(loadFiles.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload as string;
      });

    // Load inventory items
    builder
      .addCase(loadInventoryItems.pending, (state, action) => {
        if (action.meta.arg) { // refresh
          state.isLoading = true;
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(loadInventoryItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        
        if (action.payload.refresh) {
          state.inventoryItems = action.payload.items;
        } else {
          // Filter out duplicates before adding new inventory items
          const existingIds = state.inventoryItems.map(item => item.id);
          const newItems = action.payload.items.filter(item => !existingIds.includes(item.id));
          state.inventoryItems.push(...newItems);
        }
        
        state.hasMore = action.payload.hasMore;
        state.currentPage = action.payload.page;
        state.error = null;
      })
      .addCase(loadInventoryItems.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload as string;
      });

    // Delete file
    builder
      .addCase(deleteSelectedFile.pending, (state, action) => {
        const fileId = action.meta.arg;
        console.log('🗑️ REDUCER: deleteSelectedFile.pending', { fileId, currentDeletingIds: state.deletingFileIds });
        if (!state.deletingFileIds.includes(fileId)) {
          state.deletingFileIds.push(fileId);
        }
        console.log('🗑️ REDUCER: Updated deletingFileIds:', state.deletingFileIds);
      })
      .addCase(deleteSelectedFile.fulfilled, (state, action) => {
        const deletedFileId = action.payload.file_id.toString();
        console.log('🗑️ REDUCER: deleteSelectedFile.fulfilled', { deletedFileId, payload: action.payload });
        state.files = state.files.filter(file => file.id.toString() !== deletedFileId);
        // Also remove any inventory items that belonged to this file
        state.inventoryItems = state.inventoryItems.filter(item => 
          item.file_record_id?.toString() !== deletedFileId
        );
        state.selectedFile = null;
        state.error = null;
        // Remove from deleting array
        state.deletingFileIds = state.deletingFileIds.filter(id => id !== deletedFileId);
        console.log('🗑️ REDUCER: File deleted, remaining files:', state.files.length);
      })
      .addCase(deleteSelectedFile.rejected, (state, action) => {
        const fileId = action.meta.arg;
        console.log('🗑️ REDUCER: deleteSelectedFile.rejected', { fileId, error: action.payload });
        state.error = action.payload as string;
        // Remove from deleting array
        state.deletingFileIds = state.deletingFileIds.filter(id => id !== fileId);
      })

      // Delete inventory item
      .addCase(deleteInventoryItem.pending, (state, action) => {
        const itemId = action.meta.arg;
        console.log('🗑️ REDUCER: deleteInventoryItem.pending', { itemId, currentDeletingIds: state.deletingItemIds });
        if (!state.deletingItemIds.includes(itemId)) {
          state.deletingItemIds.push(itemId);
        }
        console.log('🗑️ REDUCER: Updated deletingItemIds:', state.deletingItemIds);
      })
      .addCase(deleteInventoryItem.fulfilled, (state, action) => {
        const deletedItemId = action.payload.item_id.toString();
        console.log('🗑️ REDUCER: deleteInventoryItem.fulfilled', { deletedItemId, payload: action.payload });
        state.inventoryItems = state.inventoryItems.filter(item => 
          item.id.toString() !== deletedItemId
        );
        state.error = null;
        // Remove from deleting array
        state.deletingItemIds = state.deletingItemIds.filter(id => id !== deletedItemId);
        console.log('🗑️ REDUCER: Item deleted, remaining items:', state.inventoryItems.length);
      })
      .addCase(deleteInventoryItem.rejected, (state, action) => {
        const itemId = action.meta.arg;
        console.log('🗑️ REDUCER: deleteInventoryItem.rejected', { itemId, error: action.payload });
        state.error = action.payload as string;
        // Remove from deleting array
        state.deletingItemIds = state.deletingItemIds.filter(id => id !== itemId);
      })
      
      // Update inventory item — FE-255 PR4
      //
      // Pattern: snapshot the prev row in BOTH the enhanced and legacy
      // arrays on .pending, optimistically apply the patch, and either
      // reconcile from the server response on .fulfilled or restore
      // byte-for-byte on .rejected. EditItemSheet's save handler no
      // longer needs to wait for the round-trip — the row updates the
      // moment the user taps Save, and an offline / 5xx restores cleanly.
      .addCase(updateInventoryItem.pending, (state, action) => {
        const { itemId, updateData } = action.meta.arg;
        const reqId = action.meta.requestId;

        const enhancedIdx = state.inventoryItemsEnhanced.findIndex(
          (it) => it.id === itemId,
        );
        const legacyIdx = state.inventoryItems.findIndex(
          (it) => it.id === itemId,
        );

        const prevEnhanced =
          enhancedIdx !== -1 ? state.inventoryItemsEnhanced[enhancedIdx] : null;
        const prevLegacy =
          legacyIdx !== -1 ? state.inventoryItems[legacyIdx] : null;

        state.itemEditInflight[reqId] = {
          itemId,
          prevEnhanced: prevEnhanced ? { ...prevEnhanced } : null,
          prevLegacy: prevLegacy ? { ...prevLegacy } : null,
        };

        if (prevEnhanced && enhancedIdx !== -1) {
          state.inventoryItemsEnhanced[enhancedIdx] = applyPartialPatch(
            prevEnhanced,
            updateData as Partial<InventoryItemResponse>,
          );
        }

        if (prevLegacy && legacyIdx !== -1) {
          // Map the partial patch into the legacy shape. Only the keys
          // both shapes share are forwarded; others (tags, category as
          // string-required) are coerced with the same defaults the
          // old .fulfilled path used.
          const legacyPatch: Partial<InventoryItem> = {};
          if (updateData.item_name !== undefined)
            legacyPatch.item_name = updateData.item_name;
          if (updateData.item_description !== undefined)
            legacyPatch.item_description = updateData.item_description;
          if (updateData.item_category !== undefined)
            legacyPatch.item_category = updateData.item_category;
          if (updateData.brand !== undefined)
            legacyPatch.brand = updateData.brand;
          if (updateData.estimated_value !== undefined)
            legacyPatch.estimated_value = updateData.estimated_value;
          if (updateData.condition !== undefined)
            legacyPatch.condition = updateData.condition;
          if (updateData.location !== undefined)
            legacyPatch.location = updateData.location;
          if (updateData.tags !== undefined) legacyPatch.tags = updateData.tags;
          if (updateData.notes !== undefined)
            legacyPatch.notes = updateData.notes;
          if (updateData.is_verified !== undefined)
            legacyPatch.is_verified = updateData.is_verified;
          state.inventoryItems[legacyIdx] = applyPartialPatch(
            prevLegacy,
            legacyPatch,
          );
        }

        state.error = null;
      })
      .addCase(updateInventoryItem.fulfilled, (state, action) => {
        const reqId = action.meta.requestId;
        const updatedItem = action.payload;

        // Replace the optimistic row with the BE's canonical row. We
        // don't trust the optimistic value over the server response —
        // the server may have applied normalizations (trimming, casing,
        // computed fields) the FE doesn't know about.
        const itemIndex = state.inventoryItemsEnhanced.findIndex(
          (item) => item.id === updatedItem.id,
        );
        if (itemIndex !== -1) {
          state.inventoryItemsEnhanced[itemIndex] = updatedItem;
        }

        const legacyItemIndex = state.inventoryItems.findIndex(
          (item) => item.id === updatedItem.id,
        );
        if (legacyItemIndex !== -1) {
          state.inventoryItems[legacyItemIndex] = {
            id: updatedItem.id!,
            item_name: updatedItem.item_name,
            item_description: updatedItem.item_description,
            item_category: updatedItem.item_category || '',
            estimated_value: updatedItem.estimated_value || 0,
            condition: updatedItem.condition || '',
            location: updatedItem.location || '',
            tags: updatedItem.tags || '',
            confidence_score: updatedItem.confidence_score,
            is_verified: updatedItem.is_verified,
            brand: updatedItem.brand || '',
            notes: updatedItem.notes || '',
            file_record_id: state.inventoryItems[legacyItemIndex].file_record_id,
            user_id: state.inventoryItems[legacyItemIndex].user_id,
            created_at: state.inventoryItems[legacyItemIndex].created_at,
            updated_at: new Date().toISOString(),
          };
        }

        delete state.itemEditInflight[reqId];
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateInventoryItem.rejected, (state, action) => {
        const reqId = action.meta.requestId;
        const snap = state.itemEditInflight[reqId];

        if (snap) {
          if (snap.prevEnhanced) {
            const i = state.inventoryItemsEnhanced.findIndex(
              (it) => it.id === snap.itemId,
            );
            if (i !== -1) {
              state.inventoryItemsEnhanced[i] = snap.prevEnhanced;
            }
          }
          if (snap.prevLegacy) {
            const j = state.inventoryItems.findIndex(
              (it) => it.id === snap.itemId,
            );
            if (j !== -1) {
              state.inventoryItems[j] = snap.prevLegacy;
            }
          }
          delete state.itemEditInflight[reqId];
        }

        state.error = action.payload as string;
      });

    // Load files with inventory
    builder
      .addCase(loadFilesWithInventory.pending, (state, action) => {
        if (action.meta.arg?.refresh) {
          state.isLoading = true;
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(loadFilesWithInventory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        
        if (action.payload.refresh) {
          state.filesWithInventory = action.payload.files;
        } else {
          // Filter out duplicates before adding new files
          const existingIds = state.filesWithInventory.map(file => file.id);
          const newFiles = action.payload.files.filter(file => !existingIds.includes(file.id));
          state.filesWithInventory.push(...newFiles);
        }
        
        state.hasMore = action.payload.pagination.has_next;
        state.currentPage = action.payload.pagination.current_page || 1;
        state.totalCount = action.payload.pagination.total;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(loadFilesWithInventory.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload as string;
      });

    // Sprint 2.5 (FE-9.4.1) — scenes layout pagination
    builder
      .addCase(loadScenesPage.pending, (state, action) => {
        state.scenesCache.loading = true;
        state.scenesCache.error = null;
        // Reset cursors on a 'replace' load so a stale offset doesn't
        // bleed into the next page request when toggling layouts.
        if ((action.meta.arg?.mode ?? 'replace') === 'replace') {
          state.scenesCache.nextOffset = 0;
        }
      })
      .addCase(loadScenesPage.fulfilled, (state, action) => {
        const { scenes, total, nextOffset, hasMore, mode } = action.payload;
        state.scenesCache.loading = false;
        state.scenesCache.hasLoaded = true;
        state.scenesCache.error = null;
        state.scenesCache.total = total;
        state.scenesCache.nextOffset = nextOffset;
        state.scenesCache.hasMore = hasMore;

        if (mode === 'replace') {
          state.scenesCache.scenes = dedupeScenes(scenes);
        } else {
          // Append + dedupe across the whole list (don't trust prior
          // page boundaries to be backend#37-clean).
          const seen = new Set(state.scenesCache.scenes.map((s) => s.id));
          const fresh = scenes.filter((s) => !seen.has(s.id));
          state.scenesCache.scenes = dedupeScenes([
            ...state.scenesCache.scenes,
            ...fresh,
          ]);
        }
      })
      .addCase(loadScenesPage.rejected, (state, action) => {
        state.scenesCache.loading = false;
        state.scenesCache.hasLoaded = true;
        state.scenesCache.error =
          (action.payload as string) ?? action.error.message ?? 'Error';
      });

    // Hotfix — single-scene upsert. Adds OR replaces the row by id
    // so duplicate dispatches from a remounting screen don't bloat
    // the cache. Doesn't touch pagination cursors — this is a
    // targeted fetch, not a page replacement.
    builder
      .addCase(loadSceneById.pending, (state) => {
        state.scenesCache.loading = true;
        state.scenesCache.error = null;
      })
      .addCase(loadSceneById.fulfilled, (state, action) => {
        state.scenesCache.loading = false;
        state.scenesCache.hasLoaded = true;
        state.scenesCache.error = null;
        const fresh = action.payload;
        const idx = state.scenesCache.scenes.findIndex(
          (s) => s.id === fresh.id,
        );
        if (idx === -1) {
          // Prepend — most-recent feeling order. Pagination will
          // dedupe when the user later scrolls.
          state.scenesCache.scenes = dedupeScenes([
            fresh,
            ...state.scenesCache.scenes,
          ]);
        } else {
          // Replace in place so any newly-refreshed signed URLs land.
          state.scenesCache.scenes[idx] = fresh;
        }
      })
      .addCase(loadSceneById.rejected, (state, action) => {
        state.scenesCache.loading = false;
        state.scenesCache.error =
          (action.payload as string) ?? action.error.message ?? 'Error';
      });

    // FE-210 — Scene delete (with optional cascade).
    builder.addCase(deleteScene.fulfilled, (state, action) => {
      const { fileId, cascade } = action.payload;

      // Drop the scene from the cache.
      state.scenesCache.scenes = state.scenesCache.scenes.filter(
        (s) => s.id !== fileId,
      );
      state.scenesCache.total = Math.max(0, state.scenesCache.total - 1);

      if (cascade) {
        // Every linked inventory row is gone too.
        state.inventoryItemsEnhanced = state.inventoryItemsEnhanced.filter(
          (it: any) => (it as any).source_file_id !== fileId,
        );
        state.enhancedInventoryItems = state.enhancedInventoryItems.filter(
          (it) => (it as any).source_file_id !== fileId,
        );
      } else {
        // Orphan path — items survive with source_file_id := null so
        // they no longer attribute to the dead FileRecord. Cards
        // re-render under the "Unsorted" scenes bucket on next
        // grouping.
        for (const it of state.inventoryItemsEnhanced as any[]) {
          if ((it as any).source_file_id === fileId) {
            (it as any).source_file_id = null;
          }
        }
        for (const it of state.enhancedInventoryItems as any[]) {
          if ((it as any).source_file_id === fileId) {
            (it as any).source_file_id = null;
          }
        }
      }
    });

    // Load inventory items enhanced
    builder
      .addCase(loadInventoryItemsEnhanced.pending, (state, action) => {
        if (action.meta.arg?.refresh) {
          state.isLoading = true;
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(loadInventoryItemsEnhanced.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        
        // Update filters
        state.filters = action.payload.filters;
        
        if (action.payload.refresh) {
          // Keep both arrays in sync, but inventoryItemsEnhanced stays as InventoryItemResponse for compatibility
          state.inventoryItemsEnhanced = action.payload.items as any;
          state.enhancedInventoryItems = action.payload.items;
        } else {
          // Filter out duplicates before adding new items
          const existingIds = state.inventoryItemsEnhanced.map(item => item.id);
          const newItems = action.payload.items.filter(item => !existingIds.includes(item.id));
          state.inventoryItemsEnhanced.push(...(newItems as any));
          state.enhancedInventoryItems.push(...newItems);
        }
        
        state.hasMore = action.payload.pagination.has_next;
        state.currentPage = action.payload.pagination.current_page;
        state.totalCount = action.payload.pagination.total;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(loadInventoryItemsEnhanced.rejected, (state, action) => {
        // Aborted requests mean a newer fetch is already in flight (e.g.
        // user kept typing). Don't clear isLoading or surface an error —
        // the in-flight pending action already set isLoading=true.
        if (action.meta.aborted || action.payload === 'aborted') return;
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload as string;
      });

    // Load categories
    builder
      .addCase(loadCategories.pending, (state) => {
        // Don't show loading for categories since it's background
      })
      .addCase(loadCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(loadCategories.rejected, (state, action) => {
        // Don't show error for categories since it's not critical
        logger.warn('Failed to load categories', action.payload);
      });

    // Sprint 3 / Chunk 10A — react to custody flips dispatched from the
    // custody slice. When a handoff or return resolves successfully we
    // patch `current_custodian_user_id`, `custody_since`, and
    // `custody_due_date` on the matching item in both inventory arrays
    // so Browse cards / ItemDetail render fresh custody chips without a
    // separate refetch.
    const applyCustodyFlip = (state: BrowseState, payload: CustodyFlipResponse) => {
      const { item_id, current_custodian_user_id, custody_since, custody_due_date } = payload;
      const i = state.inventoryItemsEnhanced.findIndex((x: any) => x.id === item_id);
      if (i !== -1) {
        state.inventoryItemsEnhanced[i] = {
          ...state.inventoryItemsEnhanced[i],
          current_custodian_user_id,
          custody_since,
          custody_due_date: custody_due_date ?? null,
        } as any;
      }
      const j = state.enhancedInventoryItems.findIndex((x: any) => x.id === item_id);
      if (j !== -1) {
        state.enhancedInventoryItems[j] = {
          ...state.enhancedInventoryItems[j],
          current_custodian_user_id,
          custody_since,
          custody_due_date: custody_due_date ?? null,
        } as any;
      }
      state.lastUpdated = new Date().toISOString();
    };
    // FE #155 — patch has_active_recall=true on the item immediately
    // after a recall fires. Without this, the FE's "Recall pending"
    // gate in CustodySection doesn't kick in until the next
    // /users/inventory refetch, leaving the owner staring at an
    // enabled "Recall item" row right after they tapped it.
    const applyRecallFlag = (state: BrowseState, itemId: number) => {
      const i = state.inventoryItemsEnhanced.findIndex(
        (x: any) => x.id === itemId,
      );
      if (i !== -1) {
        state.inventoryItemsEnhanced[i] = {
          ...state.inventoryItemsEnhanced[i],
          has_active_recall: true,
        } as any;
      }
      const j = state.enhancedInventoryItems.findIndex(
        (x: any) => x.id === itemId,
      );
      if (j !== -1) {
        state.enhancedInventoryItems[j] = {
          ...state.enhancedInventoryItems[j],
          has_active_recall: true,
        } as any;
      }
      state.lastUpdated = new Date().toISOString();
    };

    // FE-255 PR2 — optimistic custody helpers.
    //
    // The .pending paths flip BrowseSlice state immediately so the
    // grid / ItemDetail react to the new custody / recall shape on the
    // same frame the user tapped (instead of waiting 500ms–2s for the
    // BE round-trip). .fulfilled reconciles with the BE's authoritative
    // values (same call we used to make in .fulfilled-only). .rejected
    // restores from the snapshot AND surfaces the error via lastError
    // so the consumer can toast it.
    //
    // Snapshot shape (BrowseCustodyInflight at the top of the file)
    // captures every field we may have mutated so rollback is
    // byte-for-byte — no partial-revert edge cases.
    const findItemIndex = (state: BrowseState, itemId: number) => ({
      enh: state.inventoryItemsEnhanced.findIndex((x: any) => x.id === itemId),
      raw: state.enhancedInventoryItems.findIndex((x: any) => x.id === itemId),
    });
    const snapshotItem = (state: BrowseState, itemId: number) => {
      const idx = state.inventoryItemsEnhanced.findIndex(
        (x: any) => x.id === itemId,
      );
      if (idx === -1) return null;
      const it: any = state.inventoryItemsEnhanced[idx];
      return {
        current_custodian_user_id: it.current_custodian_user_id,
        custody_since: it.custody_since,
        custody_due_date: it.custody_due_date,
        has_active_recall: it.has_active_recall,
      };
    };
    const patchItem = (
      state: BrowseState,
      itemId: number,
      patch: Partial<BrowseCustodyInflight['prev']>,
    ) => {
      const { enh, raw } = findItemIndex(state, itemId);
      if (enh !== -1) {
        state.inventoryItemsEnhanced[enh] = {
          ...state.inventoryItemsEnhanced[enh],
          ...patch,
        } as any;
      }
      if (raw !== -1) {
        state.enhancedInventoryItems[raw] = {
          ...state.enhancedInventoryItems[raw],
          ...patch,
        } as any;
      }
      state.lastUpdated = new Date().toISOString();
    };
    const restoreInflight = (state: BrowseState, opId: string) => {
      const snap = state.custodyInflight[opId];
      if (!snap) return;
      patchItem(state, snap.itemId, snap.prev);
      delete state.custodyInflight[opId];
    };

    builder
      // handoffItem — owner pushes item to recipient. Custody flips
      // immediately at the BE; we mirror that flip on .pending.
      .addCase(handoffItem.pending, (state, action) => {
        const { itemId, request } = action.meta.arg;
        const prev = snapshotItem(state, itemId);
        if (!prev) return; // item not in cache; no optimistic possible
        state.custodyInflight[action.meta.requestId] = { itemId, prev };
        patchItem(state, itemId, {
          current_custodian_user_id: request.recipient_user_id,
          custody_since: new Date().toISOString(),
          custody_due_date: request.due_date ?? null,
        });
      })
      .addCase(handoffItem.fulfilled, (state, action) => {
        // Reconcile with BE's authoritative values (timestamps may
        // differ by < 1s; defensive in case server amended anything).
        applyCustodyFlip(state, action.payload.data);
        delete state.custodyInflight[action.meta.requestId];
      })
      .addCase(handoffItem.rejected, (state, action) => {
        restoreInflight(state, action.meta.requestId);
      })

      // returnItem — current holder hands back to owner. Custody flips
      // back; we look up the owner from the item itself (`user_id`)
      // since the BE doesn't echo it on the request.
      .addCase(returnItem.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        const idx = state.inventoryItemsEnhanced.findIndex(
          (x: any) => x.id === itemId,
        );
        if (idx === -1) return;
        const owner = (state.inventoryItemsEnhanced[idx] as any).user_id;
        const prev = snapshotItem(state, itemId);
        if (!prev) return;
        state.custodyInflight[action.meta.requestId] = { itemId, prev };
        patchItem(state, itemId, {
          current_custodian_user_id: owner ?? null,
          custody_since: null,
          custody_due_date: null,
        });
      })
      .addCase(returnItem.fulfilled, (state, action) => {
        applyCustodyFlip(state, action.payload.data);
        delete state.custodyInflight[action.meta.requestId];
      })
      .addCase(returnItem.rejected, (state, action) => {
        restoreInflight(state, action.meta.requestId);
      })

      // recallItem — owner requests forced return. Custody does NOT
      // flip; only the `has_active_recall` flag toggles.
      .addCase(recallItem.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        const prev = snapshotItem(state, itemId);
        if (!prev) return;
        state.custodyInflight[action.meta.requestId] = { itemId, prev };
        patchItem(state, itemId, { has_active_recall: true });
      })
      .addCase(recallItem.fulfilled, (state, action) => {
        // No-op for state shape — applyRecallFlag is already what we
        // did optimistically. Just clean up the inflight snapshot.
        applyRecallFlag(state, action.meta.arg.itemId);
        delete state.custodyInflight[action.meta.requestId];
      })
      .addCase(recallItem.rejected, (state, action) => {
        restoreInflight(state, action.meta.requestId);
      })

      // acknowledgeRecall — holder confirms the return. Custody flips
      // back to owner + the recall flag clears. Mirrors returnItem's
      // shape (same BE envelope).
      //
      // Pre-PR2 there was NO browseSlice listener for this mutation,
      // so the recall banner + custody chip only updated on the next
      // refetch. Adding it here closes a small UX gap as a side effect.
      .addCase(acknowledgeRecall.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        const idx = state.inventoryItemsEnhanced.findIndex(
          (x: any) => x.id === itemId,
        );
        if (idx === -1) return;
        const owner = (state.inventoryItemsEnhanced[idx] as any).user_id;
        const prev = snapshotItem(state, itemId);
        if (!prev) return;
        state.custodyInflight[action.meta.requestId] = { itemId, prev };
        patchItem(state, itemId, {
          current_custodian_user_id: owner ?? null,
          custody_since: null,
          custody_due_date: null,
          has_active_recall: false,
        });
      })
      .addCase(acknowledgeRecall.fulfilled, (state, action) => {
        applyCustodyFlip(state, action.payload.data);
        // Reconcile has_active_recall — BE's response shape is
        // CustodyFlipResponse which doesn't carry the flag, so we
        // re-assert false here. (The user is the custodian and is
        // returning the item; the recall is done.)
        const { enh, raw } = findItemIndex(state, action.payload.itemId);
        if (enh !== -1) {
          (state.inventoryItemsEnhanced[enh] as any).has_active_recall = false;
        }
        if (raw !== -1) {
          (state.enhancedInventoryItems[raw] as any).has_active_recall = false;
        }
        delete state.custodyInflight[action.meta.requestId];
      })
      .addCase(acknowledgeRecall.rejected, (state, action) => {
        restoreInflight(state, action.meta.requestId);
      });
  },
});

export const {
  setViewMode,
  setSelectedFile,
  setFilters,
  toggleFilterExpanded,
  clearError,
  resetBrowseState,
  mergeInventoryItem,
  upsertInventoryItem,
} = browseSlice.actions;

// Legacy selectors (for backward compatibility)
export const selectBrowseState = (state: RootState) => state.browse;
/** FE-248 v2 follow-up — count of RAW (pre-client-filter) inventory
 *  rows currently in the slice. When this grows but
 *  selectFilteredInventoryItems().length doesn't, the FE knows the
 *  last server page yielded zero held items and can stop
 *  auto-paginating. Without this signal we'd have nothing to wedge
 *  the held-mode infinite loop on (the filtered count stays at the
 *  same value across many "dud" page loads). */
export const selectRawInventoryCount = (state: RootState) =>
  state.browse.inventoryItemsEnhanced.length;
export const selectFiles = (state: RootState) => state.browse.files;
export const selectInventoryItems = (state: RootState) => state.browse.inventoryItems;
export const selectSelectedFile = (state: RootState) => state.browse.selectedFile;
export const selectViewMode = (state: RootState) => state.browse.viewMode;
export const selectBrowseLoading = (state: RootState) => state.browse.isLoading;
export const selectIsLoadingMore = (state: RootState) => state.browse.isLoadingMore;
export const selectHasMore = (state: RootState) => state.browse.hasMore;
export const selectBrowseError = (state: RootState) => state.browse.error;

// Enhanced selectors
export const selectFilesWithInventory = (state: RootState) => state.browse.filesWithInventory;
export const selectInventoryItemsEnhanced = (state: RootState) => state.browse.inventoryItemsEnhanced;
export const selectEnhancedInventoryItems = (state: RootState) => state.browse.enhancedInventoryItems;
export const selectFilters = (state: RootState) => state.browse.filters;
export const selectCategories = (state: RootState) => state.browse.categories;

/**
 * FE #257 — Tag corpus across every loaded item.
 *
 * Returns `[{ tag, count }]` sorted by count desc.  Splits each
 * item's `tags` field on `[,;]` (mirrors `parseTags` in
 * `groupItemsBy.ts`) and dedupes case-insensitively while
 * preserving the first observed casing as the canonical display
 * label.
 *
 * Drives the TagTypeahead dropdown (typed lookup) and the
 * FeaturedTagsStrip's count + sort order.  Cheap enough to
 * recompute per render at current corpus sizes (~hundreds of items
 * × small tag arrays each); promote to a memoized reselect
 * selector if profiles flag this hot.
 */
/**
 * FE #256 — Location corpus across every loaded item.
 *
 * Returns `[{ location, count }]` sorted by count desc.  Trims
 * whitespace, dedupes case-insensitively (preserving the first
 * observed casing as the display label), and skips items with
 * empty/null location.  Drives the LocationTypeahead dropdown.
 *
 * Cheap enough to recompute per render at current corpus sizes;
 * promote to reselect if profiles flag it as hot.
 */
export const selectAllLocations = (
  state: RootState,
): Array<{ location: string; count: number }> => {
  const items = state.browse.inventoryItemsEnhanced ?? [];
  const counts = new Map<string, { location: string; count: number }>();
  for (const it of items as any[]) {
    const raw = ((it.location ?? '') as string).trim();
    if (!raw) continue;
    const key = raw.toLowerCase();
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { location: raw, count: 1 });
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count);
};

export const selectAllTags = (
  state: RootState,
): Array<{ tag: string; count: number }> => {
  const items = state.browse.inventoryItemsEnhanced ?? [];
  const counts = new Map<string, { tag: string; count: number }>();
  for (const it of items as any[]) {
    const raw = (it.tags ?? '') as string;
    if (!raw) continue;
    for (const part of raw.split(/[,;]/)) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, { tag: trimmed, count: 1 });
      }
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count);
};
export const selectIsFilterExpanded = (state: RootState) => state.browse.isFilterExpanded;
export const selectTotalCount = (state: RootState) => state.browse.totalCount;
export const selectLastUpdated = (state: RootState) => state.browse.lastUpdated;

// All filtering (search, category, value range, verified) happens
// server-side via /users/inventory query params. The previous client-side
// substring filter on item_name/description/category/brand actively
// *removed* legitimate backend matches — the backend matches against
// fields the client doesn't render (notes, tags, OCR text), so a
// `.includes('lamp')` on the client would drop "Bessey corner clamp"
// even though the backend correctly returned it. BrowseScreen now
// dispatches a fresh fetch whenever filters change, so this selector
// just returns the server-shaped list verbatim.
export const selectFilteredInventoryItems = (state: RootState) => {
  // issue #324 / #167 — scope filtering is now SERVER-SIDE (the thunk
  // sends `scope=in_my_possession|lent_out|borrowed|all`), so the grid
  // renders the server's exact, correctly-paginated set verbatim. The
  // old client-side `.filter()` over a paginated cache dropped legitimate
  // rows that lived on later pages → empty pages + wrong counts on large
  // accounts (#40 "20 of 21"). Passthrough now.
  //
  // FE #256 — narrow exception: `filters.location` IS applied client-
  // side.  The BE's `/users/inventory` endpoint does not yet expose a
  // `location=` filter param (verified — there's no field for it in
  // the InventoryQueryParams shape).  So the typeahead's pick filters
  // the loaded page client-side until the BE catches up.  This has
  // the same "20 of 21 on late pages" footgun as the old generic
  // client-side filter, but it's an opt-in surface (user picked a
  // location) rather than always-on, and we pair it with a Manage-
  // sharing-style "Showing N of M loaded" hint if it bites in
  // practice.
  const base = state.browse.inventoryItemsEnhanced ?? [];
  const loc = state.browse.filters.location?.trim().toLowerCase();
  if (!loc) return base;
  return base.filter(
    (it: any) =>
      ((it.location ?? '') as string).trim().toLowerCase() === loc,
  );
};

/**
 * PR16 — Find an inventory row by the share_id it was claimed from.
 *
 * Used by `useNotificationDeepLink` to convert a share-related
 * notification into an ItemDetail destination when the user has
 * already claimed the share (a duplicate row with
 * `claimed_from_share_id === shareId` exists on the user's account).
 *
 * Returns null when:
 *   - The inventory cache hasn't loaded yet (cold app start)
 *   - No matching row exists (pre-claim case — caller should fall
 *     back to ShareClaim with the token)
 *
 * Caller is responsible for the fallback chain. This selector is
 * purely a lookup.
 */
export const selectInventoryItemByShareId =
  (shareId: number | null | undefined) =>
  (state: RootState): { id: number } | null => {
    if (shareId == null) return null;
    const list = state.browse.inventoryItemsEnhanced ?? [];
    for (const row of list) {
      const fromShare = (row as any).claimed_from_share_id;
      if (fromShare != null && fromShare === shareId) {
        return { id: (row as any).id as number };
      }
    }
    return null;
  };

export const selectActiveFiltersCount = (state: RootState) => {
  const filters = state.browse.filters;
  let count = 0;
  
  if (filters.search) count++;
  if (filters.category) count++;
  if (filters.is_verified !== undefined) count++;
  if (filters.min_value !== undefined || filters.max_value !== undefined) count++;
  
  return count;
};

// Delete state selectors
export const selectDeletingFileIds = (state: RootState) => state.browse.deletingFileIds;
export const selectDeletingItemIds = (state: RootState) => state.browse.deletingItemIds;
export const selectIsFileDeleting = (state: RootState, fileId: string) => 
  state.browse.deletingFileIds.includes(fileId);
/** Sprint 2.5 (FE-9.4.1) — Scenes cache selectors. */
export const selectScenesCache = (state: RootState) => state.browse.scenesCache;
export const selectScenes = (state: RootState) =>
  state.browse.scenesCache.scenes;

export const selectIsItemDeleting = (state: RootState, itemId: string) =>
  state.browse.deletingItemIds.includes(itemId);

/** Sprint 8 Phase C — items I OWN that are currently with someone
 *  else via custody handoff. Used by the Share tab's "Out on loan"
 *  section + the dedicated MyCustodyScreen.
 *
 *  The inventory cache only contains items the current user OWNS
 *  (`/users/inventory` is server-scoped). An item is "out" when its
 *  `current_custodian_user_id` is set AND differs from the owner —
 *  i.e. someone is currently holding it on the owner's behalf.
 *
 *  Lives in browseSlice because it derives entirely from the
 *  existing inventory cache — no extra fetch needed. */
export const selectLentOutItems = (state: RootState) =>
  state.browse.inventoryItemsEnhanced.filter((item: any) => {
    const owner = item.user_id ?? item.owner_user_id;
    const custodian = item.current_custodian_user_id;
    return custodian != null && owner != null && custodian !== owner;
  });

export const selectLentOutCount = (state: RootState): number =>
  selectLentOutItems(state).length;

export default browseSlice.reducer; 